import React, { useState, useEffect, useCallback } from 'react';
import {
  adminVoucherService,
  VOUCHER_STATUS_LABELS,
  VOUCHER_STATUS_COLORS,
  PAYMENT_STATUS_LABELS,
  MIN_CUSTOM_AMOUNT,
  MAX_CUSTOM_AMOUNT,
  formatCurrency,
  formatDate,
  formatDateTime,
  type VoucherListItem,
  type VoucherStatistics,
  type VoucherFilterParams,
  type AdminCreateVoucherRequest,
  type VoucherTourOffer,
  voucherService,
} from '../../services/voucherService';
import './VoucherManagement.css';
import { authFetch } from '../../config/api.config';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5174/api';

interface Customer {
  Id?: number;
  id?: number;
  FirstName?: string;
  firstName?: string;
  LastName?: string;
  lastName?: string;
  CompanyName?: string;
  companyName?: string;
  Email?: string;
  email?: string;
  Phone?: string;
  phone?: string;
}

const VoucherManagement: React.FC = () => {
  const [vouchers, setVouchers] = useState<VoucherListItem[]>([]);
  const [statistics, setStatistics] = useState<VoucherStatistics | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filters, setFilters] = useState<VoucherFilterParams>({
    page: 1,
    pageSize: 20,
    sortBy: 'createdAt',
    sortDescending: true,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCode, setSearchCode] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherListItem | null>(null);
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // 3-Punkte-Menü state
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{type: string, voucher: VoucherListItem} | null>(null);

  // Load data
  const loadVouchers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: VoucherFilterParams = {
        ...filters,
        code: searchCode || undefined,
        buyerEmail: searchEmail || undefined,
        statuses: statusFilter ? [statusFilter] : undefined,
      };

      const result = await adminVoucherService.getVouchers(params);
      setVouchers(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Gutscheine');
    } finally {
      setIsLoading(false);
    }
  }, [filters, searchCode, searchEmail, statusFilter]);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await adminVoucherService.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  }, []);

  useEffect(() => {
    loadVouchers();
    loadStatistics();
  }, [loadVouchers, loadStatistics]);

  // Click-outside Handler für Dropdown
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSearchCode('');
    setSearchEmail('');
    setStatusFilter('');
    setFilters({
      page: 1,
      pageSize: 20,
      sortBy: 'createdAt',
      sortDescending: true,
    });
  };

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDescending: prev.sortBy === field ? !prev.sortDescending : true,
      page: 1
    }));
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return '↕';
    return filters.sortDescending ? '↓' : '↑';
  };

  // Filter vouchers by search term (name/code/email)
  const filteredVouchers = vouchers.filter(v => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      v.code.toLowerCase().includes(term) ||
      v.buyerName.toLowerCase().includes(term) ||
      v.buyerEmail.toLowerCase().includes(term) ||
      (v.recipientName && v.recipientName.toLowerCase().includes(term)) ||
      (v.recipientEmail && v.recipientEmail.toLowerCase().includes(term))
    );
  });

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleViewDetails = async (voucher: VoucherListItem) => {
    try {
      const detail = await adminVoucherService.getVoucher(voucher.id);
      setSelectedVoucher(detail);
      setShowDetailModal(true);
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    }
  };

  // Download-Funktionen
  const handleDownloadInvoicePdf = async (voucher: VoucherListItem) => {
    try {
      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      const response = await authFetch(`${API_BASE_URL}/vouchers/${voucher.id}/invoice/pdf`, {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${voucher.code}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Rechnung konnte nicht heruntergeladen werden');
      }
    } catch (err) {
      alert('Fehler beim Download');
    }
  };

  const handleDownloadVoucherPdf = async (voucher: VoucherListItem) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/vouchers/${voucher.id}/pdf`, {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Gutschein-${voucher.code}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Gutschein konnte nicht heruntergeladen werden');
      }
    } catch (err) {
      alert('Fehler beim Download');
    }
  };

  // Resend-Funktionen
  const handleResendInvoice = async (voucher: VoucherListItem) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/admin/vouchers/${voucher.id}/resend-invoice`, {
        method: 'POST'
      });
      if (response.ok) {
        alert('Rechnung wurde erneut versendet');
      } else {
        const data = await response.json();
        alert(data.message || 'Fehler beim Versenden');
      }
    } catch (err) {
      alert('Fehler beim Versenden');
    }
    setConfirmAction(null);
  };

  const handleResendVoucher = async (voucher: VoucherListItem) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/admin/vouchers/${voucher.id}/resend-voucher`, {
        method: 'POST'
      });
      // T-043: Backend meldet Empfaenger bzw. Fehlergrund (502 bei fehlgeschlagenem SMTP-Versand)
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        alert(data.message || 'Gutschein wurde erneut versendet');
      } else {
        alert(data.message || 'Fehler beim Versenden');
      }
    } catch (err) {
      alert('Fehler beim Versenden');
    }
    setConfirmAction(null);
  };

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 20));

  return (
    <div className="voucher-management">
      {/* Statistics Cards */}
      {statistics && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{statistics.totalVouchers}</div>
            <div className="stat-label">Gesamt</div>
          </div>
          <div className="stat-card active">
            <div className="stat-value">{statistics.activeVouchers}</div>
            <div className="stat-label">Aktiv</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(statistics.totalValueSold)}</div>
            <div className="stat-label">Verkauft (Gesamt)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(statistics.totalOutstandingValue)}</div>
            <div className="stat-label">Offener Wert</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-value">{statistics.pendingPaymentVouchers}</div>
            <div className="stat-label">Zahlung ausstehend</div>
          </div>
          <div className="stat-card danger">
            <div className="stat-value">{statistics.vouchersExpiringSoon}</div>
            <div className="stat-label">Bald ablaufend</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="voucher-toolbar">
        <div className="search-group">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Code, Name oder E-Mail suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-main"
            />
            {searchTerm && (
              <button
                className="search-clear"
                onClick={() => setSearchTerm('')}
                title="Suche löschen"
              >
                ×
              </button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setFilters(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">Alle Status</option>
            <option value="PendingPayment">Zahlung ausstehend</option>
            <option value="Active">Aktiv</option>
            <option value="PartiallyRedeemed">Teilweise eingelöst</option>
            <option value="FullyRedeemed">Vollständig eingelöst</option>
            <option value="Expired">Abgelaufen</option>
            <option value="Cancelled">Storniert</option>
          </select>
          <button onClick={handleClearFilters} className="btn-clear">
            Alle Filter zurücksetzen
          </button>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-create">
          + Gutschein erstellen
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadVouchers}>Erneut versuchen</button>
        </div>
      )}

      {/* Voucher Table */}
      <div className="voucher-table-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Lädt Gutscheine...</p>
          </div>
        ) : vouchers.length === 0 ? (
          <div className="empty-state">
            <p>Keine Gutscheine gefunden</p>
          </div>
        ) : (
          <table className="voucher-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('code')}>
                  Code <span className="sort-icon">{getSortIcon('code')}</span>
                </th>
                <th className="sortable" onClick={() => handleSort('originalAmount')}>
                  Betrag <span className="sort-icon">{getSortIcon('originalAmount')}</span>
                </th>
                <th className="sortable" onClick={() => handleSort('remainingAmount')}>
                  Restwert <span className="sort-icon">{getSortIcon('remainingAmount')}</span>
                </th>
                <th className="sortable col-center" onClick={() => handleSort('status')}>
                  Status <span className="sort-icon">{getSortIcon('status')}</span>
                </th>
                <th className="sortable col-center" onClick={() => handleSort('paymentStatus')}>
                  Zahlung <span className="sort-icon">{getSortIcon('paymentStatus')}</span>
                </th>
                <th className="sortable" onClick={() => handleSort('buyerName')}>
                  Käufer <span className="sort-icon">{getSortIcon('buyerName')}</span>
                </th>
                <th className="sortable" onClick={() => handleSort('validUntil')}>
                  Gültig bis <span className="sort-icon">{getSortIcon('validUntil')}</span>
                </th>
                <th className="sortable" onClick={() => handleSort('createdAt')}>
                  Erstellt <span className="sort-icon">{getSortIcon('createdAt')}</span>
                </th>
                <th className="col-center">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.id}>
                  <td className="code-cell">
                    <span className="voucher-code">{voucher.code}</span>
                    {/* T-006: Fahrtbezug */}
                    {voucher.tourReferenceText && (
                      <div style={{ fontSize: '0.75em', color: '#6b7280', marginTop: 2 }}>
                        {voucher.tourReferenceText}
                      </div>
                    )}
                  </td>
                  <td>{formatCurrency(voucher.originalAmount)}</td>
                  <td>
                    <span className={voucher.remainingAmount < voucher.originalAmount ? 'partial' : ''}>
                      {formatCurrency(voucher.remainingAmount)}
                    </span>
                  </td>
                  <td className="col-center">
                    <span
                      className={`status-badge status-${VOUCHER_STATUS_COLORS[voucher.status] || 'gray'}`}
                    >
                      {VOUCHER_STATUS_LABELS[voucher.status] || voucher.status}
                    </span>
                  </td>
                  <td className="col-center">
                    <span className={`payment-badge payment-${voucher.paymentStatus.toLowerCase()}`}>
                      {PAYMENT_STATUS_LABELS[voucher.paymentStatus] || voucher.paymentStatus}
                    </span>
                  </td>
                  <td>
                    <div className="buyer-info">
                      <span className="buyer-name">{voucher.buyerName}</span>
                      <span className="buyer-email">{voucher.buyerEmail}</span>
                    </div>
                  </td>
                  <td>{voucher.validUntil ? formatDate(voucher.validUntil) : '-'}</td>
                  <td>{formatDate(voucher.createdAt)}</td>
                  <td className="actions-cell col-center">
                    <div className={`action-menu ${openDropdownId === voucher.id ? 'open' : ''}`}>
                      <button
                        className="btn-action-menu"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === voucher.id ? null : voucher.id);
                        }}
                        title="Aktionen"
                      >
                        <span>⋮</span>
                      </button>
                      {openDropdownId === voucher.id && (
                        <div className="action-dropdown">
                          {/* Details */}
                          <button className="dropdown-item" onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(voucher);
                            setOpenDropdownId(null);
                          }}>
                            <span className="dropdown-icon">👁️</span>
                            <span>Details anzeigen</span>
                          </button>

                          {/* Rechnung PDF */}
                          <button className="dropdown-item" onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadInvoicePdf(voucher);
                            setOpenDropdownId(null);
                          }}>
                            <span className="dropdown-icon">📄</span>
                            <span>Rechnung herunterladen</span>
                          </button>

                          {/* Gutschein PDF (nur wenn bezahlt) */}
                          {voucher.paymentStatus === 'Paid' && (
                            <button className="dropdown-item" onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadVoucherPdf(voucher);
                              setOpenDropdownId(null);
                            }}>
                              <span className="dropdown-icon">🎁</span>
                              <span>Gutschein herunterladen</span>
                            </button>
                          )}

                          <div className="dropdown-divider" />

                          {/* Rechnung erneut senden */}
                          <button className="dropdown-item" onClick={(e) => {
                            e.stopPropagation();
                            setConfirmAction({ type: 'resend-invoice', voucher });
                            setOpenDropdownId(null);
                          }}>
                            <span className="dropdown-icon">📧</span>
                            <span>Rechnung erneut senden</span>
                          </button>

                          {/* Gutschein erneut senden (nur wenn bezahlt) */}
                          {voucher.paymentStatus === 'Paid' && (
                            <button className="dropdown-item" onClick={(e) => {
                              e.stopPropagation();
                              setConfirmAction({ type: 'resend-voucher', voucher });
                              setOpenDropdownId(null);
                            }}>
                              <span className="dropdown-icon">🎁</span>
                              <span>Gutschein erneut senden</span>
                            </button>
                          )}

                          <div className="dropdown-divider" />

                          {/* Zahlung bestätigen (nur wenn Pending) */}
                          {voucher.paymentStatus === 'Pending' && (
                            <button className="dropdown-item" onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVoucher(voucher);
                              setShowConfirmPaymentModal(true);
                              setOpenDropdownId(null);
                            }}>
                              <span className="dropdown-icon">✅</span>
                              <span>Zahlung bestätigen</span>
                            </button>
                          )}

                          {/* Stornieren (nur wenn Active) */}
                          {voucher.status === 'Active' && (
                            <button className="dropdown-item dropdown-item-danger" onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVoucher(voucher);
                              setShowCancelModal(true);
                              setOpenDropdownId(null);
                            }}>
                              <span className="dropdown-icon">❌</span>
                              <span>Stornieren</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange((filters.page || 1) - 1)}
            disabled={filters.page === 1}
          >
            Zurück
          </button>
          <span>
            Seite {filters.page} von {totalPages}
          </span>
          <button
            onClick={() => handlePageChange((filters.page || 1) + 1)}
            disabled={filters.page === totalPages}
          >
            Weiter
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateVoucherModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadVouchers();
            loadStatistics();
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedVoucher && (
        <VoucherDetailModal
          voucher={selectedVoucher}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedVoucher(null);
          }}
        />
      )}

      {/* Confirm Payment Modal */}
      {showConfirmPaymentModal && selectedVoucher && (
        <ConfirmPaymentModal
          voucher={selectedVoucher}
          onClose={() => {
            setShowConfirmPaymentModal(false);
            setSelectedVoucher(null);
          }}
          onSuccess={() => {
            setShowConfirmPaymentModal(false);
            setSelectedVoucher(null);
            loadVouchers();
            loadStatistics();
          }}
        />
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedVoucher && (
        <CancelVoucherModal
          voucher={selectedVoucher}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedVoucher(null);
          }}
          onSuccess={() => {
            setShowCancelModal(false);
            setSelectedVoucher(null);
            loadVouchers();
            loadStatistics();
          }}
        />
      )}

      {/* Confirm Dialog für E-Mail-Versand */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal-content confirm-modal">
            <div className="modal-header">
              <h3>Bestätigung</h3>
            </div>
            <div className="modal-body">
              {confirmAction.type === 'resend-invoice' && (
                <p>Möchten Sie die Rechnung für Gutschein <strong>{confirmAction.voucher.code}</strong> erneut an <strong>{confirmAction.voucher.buyerEmail}</strong> senden?</p>
              )}
              {confirmAction.type === 'resend-voucher' && (
                <p>Möchten Sie den Gutschein <strong>{confirmAction.voucher.code}</strong> erneut an <strong>{confirmAction.voucher.buyerEmail}</strong> senden?</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setConfirmAction(null)}>
                Abbrechen
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  if (confirmAction.type === 'resend-invoice') handleResendInvoice(confirmAction.voucher);
                  if (confirmAction.type === 'resend-voucher') handleResendVoucher(confirmAction.voucher);
                }}
              >
                Senden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Voucher Modal
const CreateVoucherModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<AdminCreateVoucherRequest>({
    amount: 100,
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    recipientName: '',
    recipientEmail: '',
    personalMessage: '',
    skipPayment: false, // GEÄNDERT: Standard ist jetzt false (Zahlung erforderlich)
  });
  const [customAmount, setCustomAmount] = useState('');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // T-006: Floßfahrt verschenken (Fahrtbezug), Kundin-Screenshot 25.08.2026 im Admin-Dialog
  const [tourOffers, setTourOffers] = useState<VoucherTourOffer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<VoucherTourOffer | null>(null);
  const [useTourOffer, setUseTourOffer] = useState(false);
  const [personCount, setPersonCount] = useState(2);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const offers = await voucherService.getTourOffers();
        if (Array.isArray(offers) && offers.length > 0) {
          setTourOffers(offers);
          setSelectedOffer(offers[0]);
        }
      } catch {
        // Fahrtblock bleibt ausgeblendet
      }
    };
    loadOffers();
  }, []);

  const applyTourOffer = (persons: number, offer?: VoucherTourOffer | null) => {
    const activeOffer = offer ?? selectedOffer;
    if (!activeOffer) return;
    const computed = Math.round(persons * activeOffer.pricePerPerson * 100) / 100;
    setSelectedOffer(activeOffer);
    setPersonCount(persons);
    setUseTourOffer(true);
    setUseCustomAmount(false);
    setFormData(prev => ({ ...prev, amount: computed }));
  };

  // Kundenauswahl State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | string>('');
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  // Kunden laden
  useEffect(() => {
    const loadCustomers = async () => {
      setIsLoadingCustomers(true);
      try {
        const response = await authFetch(`${API_BASE_URL}/admin/customers?pageSize=500`, {});
        if (response.ok) {
          const data = await response.json();
          // API gibt "Customers" Array zurück (nicht "items")
          setCustomers(data.Customers || data.customers || data.items || []);
        }
      } catch (err) {
        console.error('Error loading customers:', err);
      } finally {
        setIsLoadingCustomers(false);
      }
    };
    loadCustomers();
  }, []);

  // Kundenauswahl-Handler
  const handleCustomerSelect = (value: string) => {
    setSelectedCustomerId(value);

    if (value === 'new' || value === 'manual' || value === '') {
      // Felder leeren bei manueller Eingabe oder Neuanlage
      setFormData(prev => ({ ...prev, buyerName: '', buyerEmail: '', buyerPhone: '' }));
    } else {
      // Bestehenden Kunden auswählen
      const customerId = parseInt(value);
      const customer = customers.find(c => (c.Id || c.id) === customerId);
      if (customer) {
        const firstName = customer.FirstName || customer.firstName || '';
        const lastName = customer.LastName || customer.lastName || '';
        const companyName = customer.CompanyName || customer.companyName || '';
        const fullName = `${firstName} ${lastName}`.trim() || companyName;
        setFormData(prev => ({
          ...prev,
          buyerName: fullName,
          buyerEmail: customer.Email || customer.email || '',
          buyerPhone: customer.Phone || customer.phone || ''
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const amount = useCustomAmount ? parseFloat(customAmount) : formData.amount;
      if (amount < MIN_CUSTOM_AMOUNT || amount > MAX_CUSTOM_AMOUNT) {
        throw new Error(`Betrag muss zwischen ${formatCurrency(MIN_CUSTOM_AMOUNT)} und ${formatCurrency(MAX_CUSTOM_AMOUNT)} liegen`);
      }

      // T-006: Fahrtbezug mitsenden, der Server ermittelt den Betrag aus Personen x Staffelpreis
      const tourReference = useTourOffer && selectedOffer
        ? { tourTypeId: selectedOffer.tourTypeId, personCount }
        : {};

      // T-040 (07.09.2026): leere optionale Felder NICHT als "" senden, die Backend-Prüfung
      // [EmailAddress] lehnte "" ab (HTTP 400 ohne Meldung), null bzw. weggelassen ist gültig.
      await adminVoucherService.createVoucher({
        ...formData,
        amount,
        buyerPhone: formData.buyerPhone?.trim() || undefined,
        recipientName: formData.recipientName?.trim() || undefined,
        recipientEmail: formData.recipientEmail?.trim() || undefined,
        personalMessage: formData.personalMessage?.trim() || undefined,
        ...tourReference,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erstellen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header modal-header-primary">
          <h2><span className="modal-icon">🎁</span> Gutschein erstellen</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            {/* T-006: Floßfahrt verschenken (Fahrt plus Personenzahl, Wertgutschein mit Fahrtbezug) */}
            {selectedOffer && (
              <div className="form-group">
                <label>Floßfahrt verschenken</label>
                <div
                  className={`flossfahrt-helper ${useTourOffer ? 'selected' : ''}`}
                  onClick={() => applyTourOffer(personCount)}
                  style={{
                    border: useTourOffer ? '2px solid #910F0F' : '1px solid #d1d5db',
                    borderRadius: 8,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    background: useTourOffer ? '#fdf2f2' : '#fff'
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <label htmlFor="admin-voucher-tour" style={{ margin: 0 }}>Fahrt:</label>
                    <select
                      id="admin-voucher-tour"
                      value={selectedOffer.tourTypeId}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const offer = tourOffers.find(o => o.tourTypeId === parseInt(e.target.value, 10)) ?? null;
                        if (!offer) return;
                        const maxPersons = Math.max(1, Math.min(50, Math.floor(MAX_CUSTOM_AMOUNT / offer.pricePerPerson)));
                        applyTourOffer(Math.min(personCount, maxPersons), offer);
                      }}
                    >
                      {tourOffers.map(o => (
                        <option key={o.tourTypeId} value={o.tourTypeId}>
                          {o.name} ({formatCurrency(o.pricePerPerson)} pro Person)
                        </option>
                      ))}
                    </select>
                    <label htmlFor="admin-voucher-persons" style={{ margin: 0 }}>Personen:</label>
                    <select
                      id="admin-voucher-persons"
                      value={personCount}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => applyTourOffer(parseInt(e.target.value, 10))}
                    >
                      {Array.from(
                        { length: Math.max(1, Math.min(50, Math.floor(MAX_CUSTOM_AMOUNT / selectedOffer.pricePerPerson))) },
                        (_, i) => i + 1
                      ).map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span>
                      = <strong>{formatCurrency(Math.round(personCount * selectedOffer.pricePerPerson * 100) / 100)}</strong>
                    </span>
                  </div>
                  <span className="amount-hint">
                    Wertgutschein in Höhe von Personenzahl x Preis pro Person (kleinste Staffel), frei einlösbar, Fahrt wird auf dem Gutschein vermerkt.
                    Freigabe der Fahrten unter Tour-Typen, "Als Gutschein anbieten".
                  </span>
                </div>
              </div>
            )}

            {/* Betrag in 2 Reihen */}
            <div className="form-group">
              <label>{selectedOffer ? 'Oder Betrag' : 'Betrag'}</label>
              <div className="amount-grid">
                {/* Reihe 1: 50-250 */}
                <div className="amount-row">
                  {[50, 100, 150, 200, 250].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className={`amount-btn ${formData.amount === amount && !useCustomAmount && !useTourOffer ? 'selected' : ''}`}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, amount }));
                        setUseCustomAmount(false);
                        setUseTourOffer(false);
                      }}
                    >
                      {formatCurrency(amount)}
                    </button>
                  ))}
                </div>
                {/* Reihe 2: 300-500 */}
                <div className="amount-row">
                  {[300, 350, 400, 450, 500].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className={`amount-btn ${formData.amount === amount && !useCustomAmount && !useTourOffer ? 'selected' : ''}`}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, amount }));
                        setUseCustomAmount(false);
                        setUseTourOffer(false);
                      }}
                    >
                      {formatCurrency(amount)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="custom-amount-row">
                <span className="custom-label">Oder Wunschbetrag:</span>
                <input
                  type="number"
                  placeholder="Wunschbetrag"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setUseCustomAmount(true);
                    setUseTourOffer(false);
                  }}
                />
                <span className="currency-label">EUR</span>
              </div>
              <span className="amount-hint">Min. {formatCurrency(MIN_CUSTOM_AMOUNT)} - Max. {formatCurrency(MAX_CUSTOM_AMOUNT)}</span>
            </div>

            {/* Kundenauswahl */}
            <div className="form-group customer-select-group">
              <label>Kunde auswählen</label>
              <select
                value={selectedCustomerId}
                onChange={(e) => handleCustomerSelect(e.target.value)}
                className="customer-dropdown"
                disabled={isLoadingCustomers}
              >
                <option value="">-- Bitte wählen --</option>
                <option value="manual">✏️ Manuell eingeben</option>
                <option value="new">➕ Neuen Kunden anlegen</option>
                {customers.length > 0 && (
                  <optgroup label="Bestehende Kunden">
                    {customers.map(c => {
                      const id = c.Id || c.id;
                      const firstName = c.FirstName || c.firstName || '';
                      const lastName = c.LastName || c.lastName || '';
                      const companyName = c.CompanyName || c.companyName;
                      const email = c.Email || c.email || '';
                      return (
                        <option key={id} value={id}>
                          {firstName} {lastName} {companyName ? `(${companyName})` : ''} - {email}
                        </option>
                      );
                    })}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Käufer Name *</label>
                <input
                  type="text"
                  required
                  value={formData.buyerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, buyerName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Käufer E-Mail *</label>
                <input
                  type="email"
                  required
                  value={formData.buyerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, buyerEmail: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Telefon</label>
              <input
                type="tel"
                value={formData.buyerPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, buyerPhone: e.target.value }))}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Empfänger Name</label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Empfänger E-Mail</label>
                <input
                  type="email"
                  value={formData.recipientEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Persönliche Nachricht <span className="hint-inline">(max. 3 Zeilen)</span></label>
              <textarea
                value={formData.personalMessage}
                onChange={(e) => {
                  const lines = e.target.value.split('\n');
                  if (lines.length <= 3) {
                    setFormData(prev => ({ ...prev, personalMessage: e.target.value }));
                  }
                }}
                onKeyDown={(e) => {
                  const lines = formData.personalMessage?.split('\n') || [];
                  if (e.key === 'Enter' && lines.length >= 3) {
                    e.preventDefault();
                  }
                }}
                rows={3}
                maxLength={300}
              />
            </div>

            {/* Checkbox mit verbessertem Layout */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.skipPayment}
                  onChange={(e) => setFormData(prev => ({ ...prev, skipPayment: e.target.checked }))}
                />
                <span className="checkbox-text">&nbsp;&nbsp;Zahlung überspringen (sofort aktiv)</span>
              </label>
              <p className="checkbox-hint">
                {formData.skipPayment
                  ? '✉️ Der Kunde erhält eine E-Mail mit dem aktivierten Gutschein und der Rechnung.'
                  : '✉️ Der Kunde erhält eine E-Mail mit der Rechnung und Überweisungsdaten.'}
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Abbrechen
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Wird erstellt...' : 'Gutschein erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Voucher Detail Modal
const VoucherDetailModal: React.FC<{
  voucher: VoucherListItem;
  onClose: () => void;
}> = ({ voucher, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content detail-modal">
        <div className="modal-header modal-header-primary">
          <h2><span className="modal-icon">📋</span> Gutschein Details</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <div className="modal-body">
          <div className="detail-section">
            <h3>Gutschein-Info</h3>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="label">Code:</span>
                <span className="value code">{voucher.code}</span>
              </div>
              <div className="detail-row">
                <span className="label">Originalwert:</span>
                <span className="value">{formatCurrency(voucher.originalAmount)}</span>
              </div>
              <div className="detail-row">
                <span className="label">Restwert:</span>
                <span className="value">{formatCurrency(voucher.remainingAmount)}</span>
              </div>
              {/* T-006: Fahrtbezug */}
              {voucher.tourReferenceText && (
                <div className="detail-row">
                  <span className="label">Gekauft für:</span>
                  <span className="value">{voucher.tourReferenceText}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="label">Status:</span>
                <span className={`status-badge status-${VOUCHER_STATUS_COLORS[voucher.status]}`}>
                  {VOUCHER_STATUS_LABELS[voucher.status]}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Zahlung:</span>
                <span className={`payment-badge payment-${voucher.paymentStatus.toLowerCase()}`}>
                  {PAYMENT_STATUS_LABELS[voucher.paymentStatus]}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Gültig bis:</span>
                <span className="value">{voucher.validUntil ? formatDate(voucher.validUntil) : '-'}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Käufer</h3>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="label">Name:</span>
                <span className="value">{voucher.buyerName}</span>
              </div>
              <div className="detail-row">
                <span className="label">E-Mail:</span>
                <span className="value">{voucher.buyerEmail}</span>
              </div>
            </div>
          </div>

          {voucher.recipientName && (
            <div className="detail-section">
              <h3>Empfänger</h3>
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="label">Name:</span>
                  <span className="value">{voucher.recipientName}</span>
                </div>
                {voucher.recipientEmail && (
                  <div className="detail-row">
                    <span className="label">E-Mail:</span>
                    <span className="value">{voucher.recipientEmail}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Zeitstempel</h3>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="label">Erstellt:</span>
                <span className="value">{formatDateTime(voucher.createdAt)}</span>
              </div>
              {voucher.issuedAt && (
                <div className="detail-row">
                  <span className="label">Aktiviert:</span>
                  <span className="value">{formatDateTime(voucher.issuedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {voucher.redemptions && voucher.redemptions.length > 0 && (
            <div className="detail-section">
              <h3>Einlösungen</h3>
              <table className="redemption-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Betrag</th>
                    <th>Restwert</th>
                    <th>Buchung</th>
                  </tr>
                </thead>
                <tbody>
                  {voucher.redemptions.map((r) => (
                    <tr key={r.id}>
                      <td>{formatDateTime(r.redeemedAt)}</td>
                      <td>{formatCurrency(r.amountRedeemed)}</td>
                      <td>{formatCurrency(r.remainingAfter)}</td>
                      <td>#{r.bookingId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-primary">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

// Confirm Payment Modal
const ConfirmPaymentModal: React.FC<{
  voucher: VoucherListItem;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ voucher, onClose, onSuccess }) => {
  const [paymentReference, setPaymentReference] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!paymentReference.trim()) {
      setError('Bitte geben Sie eine Zahlungsreferenz ein');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // T-043 (11.09.2026, Kundin-Befund "Gutschein kommt nach bestätigtem Zahlungseingang nicht per
      // Mail an"): Das Backend meldet jetzt, ob die Gutschein-Mail versendet wurde. Vorher wurde der
      // Dialog kommentarlos geschlossen, ein fehlgeschlagener Versand blieb unsichtbar.
      const result = await adminVoucherService.confirmPayment(voucher.id, paymentReference);
      alert(result?.message || (result?.emailSent
        ? `Zahlung bestätigt, Gutschein-Mail an ${result.emailRecipient} versendet`
        : 'Zahlung bestätigt, Gutschein-Mail konnte nicht versendet werden. Bitte über Aktionen "Gutschein erneut senden" wiederholen.'));
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Bestätigen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small-modal">
        <div className="modal-header modal-header-success">
          <h2><span className="modal-icon">✓</span> Zahlung bestätigen</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <p>
            Gutschein <strong>{voucher.code}</strong> über{' '}
            <strong>{formatCurrency(voucher.originalAmount)}</strong> als bezahlt markieren?
          </p>

          <div className="form-group">
            <label>Zahlungsreferenz / Verwendungszweck</label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="z.B. Überweisungsdatum oder Referenz"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Abbrechen
          </button>
          <button onClick={handleConfirm} disabled={isLoading} className="btn-primary">
            {isLoading ? 'Wird bestätigt...' : 'Zahlung bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Cancel Voucher Modal
const CancelVoucherModal: React.FC<{
  voucher: VoucherListItem;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ voucher, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!reason.trim()) {
      setError('Bitte geben Sie einen Stornierungsgrund ein');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await adminVoucherService.cancelVoucher(voucher.id, reason);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Stornieren');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small-modal">
        <div className="modal-header modal-header-danger">
          <h2><span className="modal-icon">⚠</span> Gutschein stornieren</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <div className="warning-box">
            <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht werden.
          </div>

          <p>
            Gutschein <strong>{voucher.code}</strong> mit Restwert{' '}
            <strong>{formatCurrency(voucher.remainingAmount)}</strong> stornieren?
          </p>

          <div className="form-group">
            <label>Stornierungsgrund *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund für die Stornierung..."
              rows={3}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Abbrechen
          </button>
          <button onClick={handleCancel} disabled={isLoading} className="btn-danger">
            {isLoading ? 'Wird storniert...' : 'Gutschein stornieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoucherManagement;
